import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw } from 'lucide-react';
import { WEEKDAYS } from '@/lib/education-levels';
import { useClassTemplates } from '@/hooks/useClassTemplates';
import { ClassTemplatesList } from './ClassTemplatesList';
import { ClassTemplatesFilter } from './ClassTemplatesFilter';
import { SyncClassTemplatesDialog } from './SyncClassTemplatesDialog';

export function ClassTemplatesByWeekday() {
  const navigate = useNavigate();
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  
  // Get current day, map to our tab keys (1-6 for Mon-Sat, 0 for Sun)
  const today = new Date().getDay();
  const defaultTab = String(today);

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [courseTypeFilter, setCourseTypeFilter] = useState('all');
  const [classTypeFilter, setClassTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');

  const weekday = Number(activeTab);
  const { data: templates = [], isLoading } = useClassTemplates(weekday);

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTeacher = 
          template.teacher_code?.toLowerCase().includes(query) ||
          template.teacher_nickname?.toLowerCase().includes(query) ||
          template.teacher_name?.toLowerCase().includes(query);
        const matchesSubject = 
          template.subject?.toLowerCase().includes(query) ||
          template.education_level?.toLowerCase().includes(query);
        const matchesLocation = 
          template.location?.toLowerCase().includes(query) ||
          template.room_name?.toLowerCase().includes(query);
        const matchesStudent = template.students?.some(
          (s) => 
            s.code?.toLowerCase().includes(query) ||
            s.nickname?.toLowerCase().includes(query) ||
            s.first_name?.toLowerCase().includes(query) ||
            s.last_name?.toLowerCase().includes(query)
        );
        
        if (!matchesTeacher && !matchesSubject && !matchesLocation && !matchesStudent) {
          return false;
        }
      }

      // Course type filter
      if (courseTypeFilter !== 'all' && template.course_type !== courseTypeFilter) {
        return false;
      }

      // Class type filter
      if (classTypeFilter !== 'all' && template.class_type !== classTypeFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        const isActive = statusFilter === 'active';
        if (template.is_active !== isActive) {
          return false;
        }
      }

      return true;
    });
  }, [templates, searchQuery, courseTypeFilter, classTypeFilter, statusFilter]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">ตารางเรียนประจำสัปดาห์</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setSyncDialogOpen(true)}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync ข้อมูล
          </Button>
          <Button onClick={() => navigate('/admin/class-templates/create')}>
            <Plus className="h-4 w-4 mr-2" />
            เพิ่มตารางเรียน
          </Button>
        </div>
      </div>

      <SyncClassTemplatesDialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen} />

      <ClassTemplatesFilter
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        courseTypeFilter={courseTypeFilter}
        onCourseTypeChange={setCourseTypeFilter}
        classTypeFilter={classTypeFilter}
        onClassTypeChange={setClassTypeFilter}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7">
          {WEEKDAYS.map((day) => (
            <TabsTrigger key={day.key} value={day.key}>
              {day.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {WEEKDAYS.map((day) => (
          <TabsContent key={day.key} value={day.key} className="mt-4">
            <ClassTemplatesList
              templates={filteredTemplates}
              isLoading={isLoading}
              weekday={day.value}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
